import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { useSnapshot } from 'valtio'
import { store, actions, createList } from '@/store'
import { haptics } from '@/lib/haptics'
import { toast } from 'sonner'
import { Trash2, Copy } from 'lucide-react'
import { useSounds } from '@/hooks/use-sounds'
import { showUndoToast } from '@/lib/toast-utils'
export function useListOperations(selectedListId, setSelectedListId) {
  const snap = useSnapshot(store)
  const lists = useMemo(() => snap.lists, [snap.lists])

  // List creation state
  const [isCreatingList, setIsCreatingList] = useState(false)
  const [newListName, setNewListName] = useState('')
  const [newListEmoji, setNewListEmoji] = useState()
  const [newListColor, setNewListColor] = useState()

  // List renaming state
  const [renamingListId, setRenamingListId] = useState(null)
  const [renameValue, setRenameValue] = useState('')

  // List selection state
  const [selectedListIds, setSelectedListIds] = useState(new Set())
  const [isSelectingLists, setIsSelectingLists] = useState(false)
  const listSelectionSnapshotRef = useRef(selectedListIds)
  const shouldIgnoreNextGlobalClickRef = useRef(false)
  useEffect(() => {
    listSelectionSnapshotRef.current = selectedListIds
  }, [selectedListIds])

  useEffect(() => {
    const handleGlobalClick = (e) => {
      if (shouldIgnoreNextGlobalClickRef.current) {
        shouldIgnoreNextGlobalClickRef.current = false
        return
      }
      if (selectedListIds.size > 0) {
        const target = e.target
        if (!(target instanceof Element)) return
        const isInsideListSection = target.closest('#sidebar-list-section')
        const isListItem = target.closest('.sidebar-list-item')
        const isSelectionArea = target.closest('.selection-area')
        const isInteractiveInListSection =
          target.closest('button') || target.closest('input') || target.closest('[contenteditable]')
        const isMenu = target.closest('[role="menu"]') || target.closest('[role="dialog"]')
        const isModifier = e.metaKey || e.ctrlKey || e.shiftKey

        if (isSelectionArea || isModifier) return

        if (isInsideListSection && !isListItem && !isInteractiveInListSection && !isMenu) {
          setSelectedListIds(new Set())
          return
        }

        if (!isListItem && !isInsideListSection && !isMenu) {
          setSelectedListIds(new Set())
        }
      }
    }

    document.addEventListener('click', handleGlobalClick)
    return () => document.removeEventListener('click', handleGlobalClick)
  }, [selectedListIds])

  const suppressNextSelectionClear = useCallback(() => {
    shouldIgnoreNextGlobalClickRef.current = true
  }, [])
  const { playDeleteSound } = useSounds()
  const handleAddList = useCallback(() => {
    if (!newListName.trim()) return
    const list = createList(newListName.trim(), {
      emoji: newListEmoji,
      color: newListColor,
    })
    window.record('list_created', {
      list_id: list.id,
      name: list.name,
      has_emoji: Boolean(list.emoji),
      has_color: Boolean(list.color),
    })
    actions.addList(list)
    setSelectedListId(list.id)
    setIsCreatingList(false)
    setNewListName('')
    setNewListEmoji(undefined)
    setNewListColor(undefined)
    haptics.impact()
  }, [newListName, newListEmoji, newListColor, setSelectedListId])
  const handleRenameList = useCallback(
    (listId) => {
      if (!renameValue.trim()) return
      actions.updateList(listId, {
        name: renameValue.trim(),
      })
      setRenamingListId(null)
      haptics.impact()
    },
    [renameValue],
  )
  const handleToggleListPin = useCallback(
    (listId) => {
      const list = lists.find((l) => l.id === listId)
      if (list) {
        const willBePinned = !list.pinned
        actions.updateList(listId, {
          pinned: willBePinned,
        })
        haptics.impact()
        toast(willBePinned ? 'List pinned' : 'List unpinned')
      }
    },
    [lists],
  )
  const handleDuplicateList = useCallback(
    (listId) => {
      const listIndex = lists.findIndex((l) => l.id === listId)
      if (listIndex === -1) return
      const list = lists[listIndex]

      const newList = createList(`${list.name} Copy`, {
        color: list.color,
        emoji: list.emoji,
      })
      actions.insertList(newList, listIndex + 1)
      const newListId = newList.id
      const tasks = store.todos.filter((t) => t.listId === listId)
      tasks.forEach((t) => {
        actions.addTodo({
          ...t,
          id: crypto.randomUUID(),
          listId: newListId,
          timeSpent: 0,
          subtasks: t.subtasks.map((s) => ({
            ...s,
          })),
          comments: t.comments.map((c) => ({
            ...c,
            id: crypto.randomUUID(),
          })),
          reactions: {},
          lastStartTime: undefined,
        })
      })
      haptics.success()
      toast('List duplicated', {
        icon: <Copy className="h-4 w-4" />,
      })
    },
    [lists],
  )
  const bulkDeleteLists = useCallback(() => {
    const selected = new Set(selectedListIds)
    selected.forEach((id) => {
      const list = lists.find((l) => l.id === id)
      if (list) {
        actions.deleteList(id)
      }
    })
    if (selected.has(selectedListId)) {
      setSelectedListId('all')
    }
    setSelectedListIds(new Set())
    haptics.success()
    playDeleteSound()
    showUndoToast(`${selected.size} lists deleted`, <Trash2 className="h-4 w-4 text-destructive" />)
  }, [selectedListIds, lists, selectedListId, playDeleteSound, setSelectedListId])
  const bulkDuplicateLists = useCallback(() => {
    const selected = Array.from(selectedListIds)
    // Sort by index descending to preserve insertion order relative to originals
    // When duplicating, we insert after. Processing bottom-up prevents index shifts from affecting earlier items.
    selected.sort((a, b) => {
      const indexA = lists.findIndex((l) => l.id === a)
      const indexB = lists.findIndex((l) => l.id === b)
      return indexB - indexA
    })

    selected.forEach((id) => {
      handleDuplicateList(id)
    })
    setSelectedListIds(new Set())
  }, [selectedListIds, handleDuplicateList, lists])
  return {
    isCreatingList,
    setIsCreatingList,
    newListName,
    setNewListName,
    newListEmoji,
    setNewListEmoji,
    newListColor,
    setNewListColor,
    renamingListId,
    setRenamingListId,
    renameValue,
    setRenameValue,
    selectedListIds,
    setSelectedListIds,
    listSelectionSnapshotRef,
    isSelectingLists,
    setIsSelectingLists,
    suppressNextSelectionClear,
    handleAddList,
    handleRenameList,
    handleToggleListPin,
    handleDuplicateList,
    bulkDeleteLists,
    bulkDuplicateLists,
  }
}
